// src/components/PhantomSigningRedirect.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import phantomDeeplinkService from '../services/phantom-deeplink.service';

interface ErrorDetails {
  message: string;
  code?: string;
  details?: string;
}

interface DebugInfo {
  urlParams: Record<string, string>;
  localStorage: Record<string, any>;
  timestamp: string;
  phantomResponse?: any;
  error?: any;
}

const PhantomSigningRedirect: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing transaction...');
  const [error, setError] = useState<ErrorDetails | null>(null);
  const [progress, setProgress] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [manualMode, setManualMode] = useState(false);

  useEffect(() => {
    let progressInterval: NodeJS.Timeout;

    const handleSigningRedirect = async () => {
      // Start progress animation
      progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return 90;
          return prev + 10;
        });
      }, 200);

      // Collect debug information
      const urlParams = new URLSearchParams(window.location.search);
      const urlParamsObj: Record<string, string> = {};
      urlParams.forEach((value, key) => {
        urlParamsObj[key] = value;
      });

      const localStorageData: Record<string, any> = {};
      const relevantKeys = [
        'phantom_pending_transaction',
        'phantom_session',
        'phantom_public_key',
        'phantom_funding_state',
        'pending_bundle_funding',
        'pending_funding_tx'
      ];
      
      relevantKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            localStorageData[key] = JSON.parse(value);
          } catch {
            localStorageData[key] = value;
          }
        }
      });

      const debug: DebugInfo = {
        urlParams: urlParamsObj,
        localStorage: localStorageData,
        timestamp: new Date().toISOString()
      };

      console.log('🔍 Debug Info:', debug);
      setDebugInfo(debug);

      try {
        // Check for Phantom error parameters
        const phantomErrorMessage = urlParams.get('errorMessage');
        const phantomErrorCode = urlParams.get('errorCode');

        if (phantomErrorMessage || phantomErrorCode) {
          clearInterval(progressInterval);
          setProgress(100);
          
          // Interpret error codes
          let userFriendlyMessage = phantomErrorMessage || 'Transaction failed';
          let debugMessage = `Phantom Error - Code: ${phantomErrorCode}, Message: ${phantomErrorMessage}`;
          
          if (phantomErrorCode === '-32000') {
            userFriendlyMessage = 'Transaction cancelled by user';
            debugMessage += ' (User cancelled)';
          } else if (phantomErrorCode === '-32603') {
            userFriendlyMessage = 'Transaction failed. Transaction might be too large or invalid.';
            debugMessage += ' (Internal error - check transaction size/validity)';
          } else if (phantomErrorCode === '-32602') {
            userFriendlyMessage = 'Invalid transaction parameters.';
            debugMessage += ' (Invalid params)';
          }

          setStatus('error');
          setMessage('Transaction Failed');
          setError({
            message: userFriendlyMessage,
            code: phantomErrorCode || undefined,
            details: debugMessage
          });

          debug.phantomResponse = { error: true, code: phantomErrorCode, message: phantomErrorMessage };
          setDebugInfo(debug);

          localStorage.setItem(
            'phantom_transaction_result',
            JSON.stringify({
              type: 'error',
              error: { message: userFriendlyMessage, code: phantomErrorCode },
              timestamp: Date.now()
            })
          );

          localStorage.removeItem('phantom_pending_transaction');
          setManualMode(true);
          return;
        }

        // Check for success parameters
        const hasData = urlParams.has('data');
        const hasNonce = urlParams.has('nonce');

        if (!hasData || !hasNonce) {
          if (urlParams.toString() === '') {
            clearInterval(progressInterval);
            setProgress(100);
            
            setStatus('error');
            setMessage('Transaction Cancelled');
            setError({
              message: 'No response from Phantom. Transaction may have been cancelled.',
              code: 'NO_RESPONSE'
            });

            debug.phantomResponse = { error: true, message: 'No URL parameters' };
            setDebugInfo(debug);

            localStorage.setItem(
              'phantom_transaction_result',
              JSON.stringify({
                type: 'cancelled',
                timestamp: Date.now()
              })
            );

            setManualMode(true);
            return;
          }
        }

        // Process the successful response
        setMessage('Decrypting and verifying transaction...');
        const result = await phantomDeeplinkService.handleSigningRedirect();

        clearInterval(progressInterval);
        setProgress(100);

        debug.phantomResponse = { success: true, resultType: typeof result };
        
        if (typeof result === 'string') {
          setStatus('success');
          setMessage('Transaction Successful!');
          
          debug.phantomResponse.signature = result.substring(0, 20) + '...';
          
          localStorage.setItem(
            'phantom_transaction_result',
            JSON.stringify({
              type: 'transaction_signature',
              signature: result,
              timestamp: Date.now()
            })
          );

          // Check if this was a bundle funding operation
          const fundingState = localStorage.getItem('phantom_funding_state');
          const pendingFunding = localStorage.getItem('pending_bundle_funding');
          
          if (fundingState || pendingFunding) {
            try {
              const state = fundingState ? JSON.parse(fundingState) : JSON.parse(pendingFunding!);
              setMessage(`Successfully processed funding for ${state.walletCount || state.funded || 0} wallets!`);
              debug.phantomResponse.fundingState = state;
            } catch (e) {
              console.error('Failed to parse funding state:', e);
            }
          }

        } else if (Array.isArray(result)) {
          setStatus('success');
          setMessage(`${result.length} transactions signed successfully!`);
          
          debug.phantomResponse.transactionCount = result.length;
          
          localStorage.setItem(
            'phantom_transaction_result',
            JSON.stringify({
              type: 'signed_transactions',
              transactions: result,
              timestamp: Date.now()
            })
          );
        } else {
          setStatus('success');
          setMessage('Signature successful!');
          
          localStorage.setItem(
            'phantom_transaction_result',
            JSON.stringify({
              type: 'message_signature',
              signature: result,
              timestamp: Date.now()
            })
          );
        }

        setDebugInfo(debug);
        localStorage.removeItem('phantom_pending_transaction');

        // Don't auto-redirect in debug mode
        setManualMode(true);

      } catch (err: any) {
        clearInterval(progressInterval);
        setProgress(100);
        
        console.error('Failed to process transaction:', err);
        
        debug.error = {
          message: err.message,
          stack: err.stack,
          name: err.name
        };
        setDebugInfo(debug);
        
        setStatus('error');
        setMessage('Processing Failed');
        setError({
          message: 'Failed to process the transaction response.',
          details: err.message
        });

        localStorage.setItem(
          'phantom_transaction_result',
          JSON.stringify({
            type: 'processing_error',
            error: { message: err.message },
            timestamp: Date.now()
          })
        );

        localStorage.removeItem('phantom_pending_transaction');
        setManualMode(true);
      }
    };

    handleSigningRedirect();

    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [navigate]);

  const copyDebugInfo = () => {
    if (debugInfo) {
      navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
      alert('Debug info copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-black flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-gray-800/90 backdrop-blur-sm rounded-2xl p-8 shadow-2xl">
          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {status === 'processing' && (
              <div className="relative">
                <div className="w-20 h-20 rounded-full border-4 border-gray-700"></div>
                <div 
                  className="absolute inset-0 w-20 h-20 rounded-full border-4 border-purple-500 border-t-transparent animate-spin"
                ></div>
              </div>
            )}
            
            {status === 'success' && (
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center animate-pulse">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            
            {status === 'error' && (
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
          </div>

          {/* Message */}
          <h2 className="text-2xl font-bold text-center text-white mb-2">
            {message}
          </h2>

          {/* Error Details */}
          {error && (
            <div className="mt-4 p-4 bg-red-900/30 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm text-center">{error.message}</p>
              {error.code && (
                <p className="text-red-500/70 text-xs text-center mt-1">
                  Error Code: {error.code}
                </p>
              )}
              {error.details && (
                <p className="text-red-500/70 text-xs text-center mt-1">
                  Details: {error.details}
                </p>
              )}
            </div>
          )}

          {/* Success Details */}
          {status === 'success' && (
            <div className="mt-4 p-4 bg-green-900/30 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-sm text-center">
                Transaction processed successfully
              </p>
              {debugInfo?.phantomResponse?.signature && (
                <p className="text-green-400/70 text-xs text-center mt-1">
                  Signature: {debugInfo.phantomResponse.signature}
                </p>
              )}
            </div>
          )}

          {/* Processing Details */}
          {status === 'processing' && (
            <div className="mt-6">
              <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-gray-400 text-xs text-center mt-3">
                Processing transaction response from Phantom...
              </p>
            </div>
          )}

          {/* Manual Actions */}
          {manualMode && (
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
              >
                Retry
              </button>
              <button
                onClick={() => navigate('/tools', { replace: true })}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors"
              >
                Back to Tools
              </button>
              <button
                onClick={() => setShowDebug(!showDebug)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
              >
                {showDebug ? 'Hide' : 'Show'} Debug
              </button>
            </div>
          )}

          {/* Debug Panel */}
          {showDebug && debugInfo && (
            <div className="mt-6 p-4 bg-gray-900/50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-bold text-gray-300">Debug Information</h3>
                <button
                  onClick={copyDebugInfo}
                  className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-xs text-white rounded"
                >
                  Copy
                </button>
              </div>
              <pre className="text-xs text-gray-400 overflow-auto max-h-64">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Help Text */}
        <p className="text-center text-gray-500 text-xs mt-4">
          {status === 'processing' && 'Decrypting response from Phantom...'}
          {status === 'success' && 'Transaction completed successfully'}
          {status === 'error' && 'Check debug info for details'}
        </p>
      </div>
    </div>
  );
};

export default PhantomSigningRedirect;